// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/FiscalDataSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, theme } from 'antd';
import { AuditOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FiscalDataSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const fd = data?.fiscalData;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = fd && (
    fd.ncm ||
    fd.cest ||
    fd.cfop ||
    fd.icms?.origin !== undefined ||
    fd.icms?.cst ||
    fd.icms?.csosn ||
    fd.pis?.cst ||
    fd.cofins?.cst
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['fiscal-data']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'fiscal-data',
          label: (
            <Flex align="center" gap={8}>
              <AuditOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('fiscal_data')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {fd.ncm && (
                <Descriptions.Item label="NCM">
                  {renderValue(fd.ncm)}
                </Descriptions.Item>
              )}
              {fd.cest && (
                <Descriptions.Item label="CEST">
                  {renderValue(fd.cest)}
                </Descriptions.Item>
              )}
              {fd.cfop && (
                <Descriptions.Item label="CFOP">
                  {renderValue(fd.cfop)}
                </Descriptions.Item>
              )}
              {fd.icms?.origin !== undefined && (
                <Descriptions.Item label={translateX('origin')}>
                  <Tag color={fd.icms.origin === 0 ? 'green' : 'orange'}>
                    {fd.icms.origin === 0 ? translateX('national') : translateX('imported')}
                  </Tag>
                </Descriptions.Item>
              )}
              {fd.icms?.csosn && (
                <Descriptions.Item label="CSOSN">
                  {renderValue(fd.icms.csosn)}
                </Descriptions.Item>
              )}
              {fd.icms?.cst && (
                <Descriptions.Item label="CST ICMS">
                  {renderValue(fd.icms.cst)}
                </Descriptions.Item>
              )}
              {fd.icms?.rate !== undefined && (
                <Descriptions.Item label={translateX('icms_rate')}>
                  {fd.icms.rate}%
                </Descriptions.Item>
              )}
              {fd.pis?.cst && (
                <Descriptions.Item label="CST PIS">
                  {renderValue(fd.pis.cst)}
                </Descriptions.Item>
              )}
              {fd.pis?.rate !== undefined && (
                <Descriptions.Item label={translateX('pis_rate')}>
                  {fd.pis.rate}%
                </Descriptions.Item>
              )}
              {fd.cofins?.cst && (
                <Descriptions.Item label="CST COFINS">
                  {renderValue(fd.cofins.cst)}
                </Descriptions.Item>
              )}
              {fd.cofins?.rate !== undefined && (
                <Descriptions.Item label={translateX('cofins_rate')}>
                  {fd.cofins.rate}%
                </Descriptions.Item>
              )}
              {fd.hasSt !== undefined && (
                <Descriptions.Item label={translateX('has_st')}>
                  {fd.hasSt ? (
                    <Tag color="orange">{translateX('yes')}</Tag>
                  ) : (
                    <Tag color="default">{translateX('no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
              {fd.hasTaxBenefit !== undefined && (
                <Descriptions.Item label={translateX('has_tax_benefit')}>
                  {fd.hasTaxBenefit ? (
                    <Tag color="green">{translateX('yes')}</Tag>
                  ) : (
                    <Tag color="default">{translateX('no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
              {fd.isTrackable !== undefined && (
                <Descriptions.Item label={translateX('trackable')}>
                  {fd.isTrackable ? (
                    <Tag color="green">{translateX('yes')}</Tag>
                  ) : (
                    <Tag color="default">{translateX('no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FiscalDataSection;
